import React, { useCallback, useContext, useRef, useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { validateConnection } from './utils/flowValidation';
import ReactFlow, {
  ReactFlowProvider,
  useNodesState,
  useEdgesState,
  addEdge,
  Background,
  Controls,
  MiniMap,
  useReactFlow,
} from 'reactflow';
import 'reactflow/dist/style.css';

import PurebrandLoader from 'components/Loader/PurebrandLoader';
import { Download, Upload, X, Save, Trash2 } from 'lucide-react';
import { message, Modal } from 'antd';

import GrapesJsEditor from '../EmailEditorComparison/GrapesJsEditor';
import {
  getWorkflow,
  updateWorkflow,
  deleteWorkflowDraft,
  updateTemplate,
  createTemplate,
} from 'Services/storeService';
import { processTemplateImages } from 'utils/imageUploadUtil';

// ─── Split files ──────────────────────────────────────────────────────────────
import {
  serializeGraph,
  formatRelativeTime,
  getId,
  setIdCounter,
  initialAvailableComponents,
  DEFAULT_NODE_STYLE,
} from './constants';
import { nodeTypes } from './nodes';
import ConfigPanel from './components/ConfigPanel';
import SmsEditorModal from './components/SmsEditorModal';
import useFlowLoader from './hooks/useFlowLoader';
import useAutoSave from './hooks/useAutoSave';
import logger from 'utils/logger';
import AuthContext from 'context/AuthContext';
import IncompleteProfile from 'Pages/dashboard/Profile/IncompleteProfile';
import useCan from 'hooks/useCan';
import { CAPABILITIES } from 'utils/capabilities';

// Maps a sidebar palette item's nodeType to its backend NodeDefinition code.
// Structural nodes (trigger, description/label) are not governed by
// FlowTypeAllowedNode, so they are intentionally absent here and always shown.
const PALETTE_NODE_CODE = {
  emailNode: 'email',
  smsNode: 'sms',
  waitNode: 'wait',
  conditionNode: 'condition',
};

// Keeps only palette items whose backend node code is permitted for this flow.
// allowedNodes === null means "not loaded / new flow" → keep everything.
const filterPaletteByAllowedNodes = (items, allowedNodes) => {
  if (!Array.isArray(allowedNodes)) return items;
  return items.filter((item) => {
    const code = PALETTE_NODE_CODE[item.nodeType];
    return !code || allowedNodes.includes(code);
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// WorkflowBuilder — main canvas component (must be inside ReactFlowProvider)
// ─────────────────────────────────────────────────────────────────────────────
const WorkflowBuilder = () => {
  const navigate = useNavigate();
  const reactFlowWrapper = useRef(null);
  const fileInputRef = useRef(null);
  const { project } = useReactFlow();
  const [searchParams] = useSearchParams();
  const flowId = searchParams.get('flowId');
  const fromOnboarding = searchParams.get('fromOnboarding') === 'true';
  // View-only roles can open a flow but every write (auto-save, publish,
  // delete draft, template save, node add/remove) is withheld: a 403 from the
  // backend would log the user out.
  const { allowed: canManage } = useCan(CAPABILITIES.COMMUNICATIONS_MANAGE);

  // ── Flow state ──────────────────────────────────────────────────────────────
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [isLoadingFlow, setIsLoadingFlow] = useState(!!flowId);

  // ── UI state ────────────────────────────────────────────────────────────────
  const [showConfig, setShowConfig] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);
  const [showEmailEditor, setShowEmailEditor] = useState(false);
  const [editingEmailNode, setEditingEmailNode] = useState(null);
  const emailEditorRef = useRef(null);
  const [showSmsEditor, setShowSmsEditor] = useState(false);
  const [editingSmsNode, setEditingSmsNode] = useState(null);

  // ── API / workflow state ────────────────────────────────────────────────────
  const [availableNodes, setAvailableNodes] = useState(initialAvailableComponents);
  const [workflowId, setWorkflowId] = useState(null);
  const [savedWorkflows, setSavedWorkflows] = useState([]);

  // ── Auto-save state ─────────────────────────────────────────────────────────
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState('saved');
  const [workflowVersion, setWorkflowVersion] = useState(null); // 'draft' | 'published' | null
  const [lastSavedVersion, setLastSavedVersion] = useState(null);
  const [workflowName, setWorkflowName] = useState('');
  const [flowTypeCode, setFlowTypeCode] = useState(null);
  // Node codes this flow type permits (from FlowTypeAllowedNode in the DB).
  // null = not yet loaded / new flow → show the full palette (backward compatible).
  const [allowedNodes, setAllowedNodes] = useState(null);
  // When true (DB-driven, from flow_type.structure_locked) the graph structure is
  // fixed: no adding/deleting nodes, no rewiring, no editing wait/condition/action
  // config. Store admins may only edit the message content of email/SMS nodes.
  const [structureLocked, setStructureLocked] = useState(false);

  // ── Refs for interval-based auto-save ──────────────────────────────────────
  const nodesRef = useRef(nodes);
  const edgesRef = useRef(edges);
  const lastSavedGraphRef = useRef(null);
  const isSavingRef = useRef(false);
  const workflowIdRef = useRef(null);
  const isFlowLoadedRef = useRef(false);
  const pendingSnapshotRef = useRef(false);

  // Keep refs in sync
  useEffect(() => { nodesRef.current = nodes; }, [nodes]);
  useEffect(() => { edgesRef.current = edges; }, [edges]);
  useEffect(() => { workflowIdRef.current = workflowId; }, [workflowId]);

  // ── Custom hooks ────────────────────────────────────────────────────────────
  useFlowLoader({
    flowId, setNodes, setEdges,
    setWorkflowId, setWorkflowVersion, setLastSavedVersion, setLastSavedAt,
    isFlowLoadedRef, lastSavedGraphRef, pendingSnapshotRef,
    setSaveStatus, setIsLoadingFlow, setWorkflowName, setFlowTypeCode,
    setAllowedNodes, setStructureLocked,
  });

  useAutoSave({
    workflowIdRef, nodesRef, edgesRef, isSavingRef,
    isFlowLoadedRef, lastSavedGraphRef,
    setSaveStatus, setIsSaving, setLastSavedAt, setWorkflowVersion,
    enabled: canManage,
  });

  // ── Snapshot nodes after React processes them (prevents false auto-save diffs)
  useEffect(() => {
    if (pendingSnapshotRef.current) {
      lastSavedGraphRef.current = serializeGraph(nodes, edges);
      pendingSnapshotRef.current = false;
    }
  }, [nodes, edges]);

  // ── Auto-update relative timestamp display every minute ────────────────────
  useEffect(() => {
    if (!lastSavedAt) return;
    const interval = setInterval(() => setLastSavedAt(new Date(lastSavedAt.getTime())), 60000);
    return () => clearInterval(interval);
  }, [lastSavedAt]);

  // ── Populate sidebar drag items ─────────────────────────────────────────────
  useEffect(() => {
    const actions = [
      { label: 'Trigger', nodeType: 'triggerNode', icon: '⚡' },
      { label: 'Description', nodeType: 'labelNode', icon: '📝' },
      { label: 'Email', nodeType: 'emailNode', icon: '📧' },
      { label: 'SMS', nodeType: 'smsNode', icon: '💬' },
      { label: 'Condition', nodeType: 'conditionNode', icon: '🔀' },
    ];
    const timings = [
      { label: 'Time delay', nodeType: 'waitNode', icon: '⏱️' },
    ];

    setAvailableNodes({
      actions: filterPaletteByAllowedNodes(actions, allowedNodes),
      timings: filterPaletteByAllowedNodes(timings, allowedNodes),
    });
  }, [allowedNodes]);

  // ── Load saved workflows from localStorage ─────────────────────────────────
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('workflows') || '[]');
      setSavedWorkflows(saved);
    } catch { /* ignore */ }
  }, []);

  // ── Listen for edit/delete events dispatched by node cards ─────────────────
  useEffect(() => {
    const handleEditNode = (event) => {
      const node = nodes.find((n) => n.id === event.detail.nodeId);
      // Trigger nodes are fixed for every flow and cannot be edited.
      if (node && node.type !== 'triggerNode') { setSelectedNode(node); setShowConfig(true); }
    };

    const handleDeleteNode = (event) => {
      const { nodeId } = event.detail;
      if (!canManage) return;
      // Structure-locked flows: nodes cannot be removed.
      if (structureLocked) {
        message.warning("This workflow's structure is locked. You can edit message content only.");
        return;
      }
      const target = nodes.find((n) => n.id === nodeId);
      if (target?.type === 'triggerNode') {
        if (nodes.filter((n) => n.type === 'triggerNode').length <= 1) {
          message.warning('Cannot delete the only trigger node. The workflow must have a trigger.');
          return;
        }
      }
      setNodes((nds) => nds.filter((n) => n.id !== nodeId));
      setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId));
    };

    window.addEventListener('editNode', handleEditNode);
    window.addEventListener('deleteNode', handleDeleteNode);
    return () => {
      window.removeEventListener('editNode', handleEditNode);
      window.removeEventListener('deleteNode', handleDeleteNode);
    };
  }, [nodes, setNodes, setEdges, structureLocked, canManage]);

  // ── ReactFlow callbacks ─────────────────────────────────────────────────────
  const onConnect = useCallback(
    (params) => {
      if (!canManage) return;
      if (structureLocked) { message.warning("This workflow's structure is locked. You can edit message content only."); return; }
      const validation = validateConnection(params, nodes, edges, workflowName);
      if (!validation.isValid) { message.warning(validation.message); return; }
      setEdges((eds) => addEdge({ ...params, type: 'default', animated: false }, eds));
    },
    [setEdges, nodes, edges, workflowName, structureLocked, canManage],
  );

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    async (event) => {
      event.preventDefault();
      if (!canManage) return;
      if (structureLocked) { message.warning("This workflow's structure is locked. You can edit message content only."); return; }
      const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
      const compStr = event.dataTransfer.getData('application/reactflow');
      if (!compStr) return;

      // Calculate position BEFORE any awaits (SyntheticEvent is nullified after await)
      const position = project({
        x: event.clientX - reactFlowBounds.left,
        y: event.clientY - reactFlowBounds.top,
      });

      const comp = JSON.parse(compStr);

      if (comp.nodeType === 'triggerNode' && nodes.find((n) => n.type === 'triggerNode')) {
        message.warning('Only one trigger node is allowed per workflow.');
        return;
      }

      let defaultData = { label: comp.label, nodeType: comp.label.toLowerCase() };

      if (comp.nodeType === 'triggerNode') {
        defaultData = { label: 'Trigger', subtitle: 'Define your trigger condition', nodeType: 'trigger' };
      } else if (comp.nodeType === 'waitNode') {
        defaultData = { label: 'Wait 10 days', duration: 10, durationType: 'days', nodeType: 'wait' };
      } else if (comp.nodeType === 'emailNode') {
        let templateId = uuidv4();
        try {
          const res = await createTemplate(templateId, 'email', 'New Email');
          templateId = res.data?.data?.template?.id || res.data?.data?.id || res.data?.id || templateId;
        } catch (e) {
          logger.warn('[onDrop] Could not pre-register email template:', e?.message);
        }
        defaultData = { label: 'New Email', subtitle: 'Email content', nodeType: 'email', subject: '', templateId: templateId };
      } else if (comp.nodeType === 'smsNode') {
        let templateId = uuidv4();
        try {
          const res = await createTemplate(templateId, 'sms', 'New SMS');
          templateId = res.data?.data?.template?.id || res.data?.data?.id || res.data?.id || templateId;
        } catch (e) {
          logger.warn('[onDrop] Could not pre-register SMS template:', e?.message);
        }
        defaultData = { label: 'New SMS', subtitle: 'SMS content', nodeType: 'sms', templateId: templateId };
      } else if (comp.nodeType === 'labelNode') {
        defaultData = { label: 'Description text', nodeType: 'label' };
      } else if (comp.nodeType === 'conditionNode') {
        defaultData = {
          label: 'Condition',
          subtitle: 'Branch based on a variable',
          nodeType: 'condition',
          variable: '',
          operator: 'equals',
          value: '',
        };
      }

      setNodes((nds) => nds.concat({
        id: getId(),
        type: comp.nodeType,
        position,
        data: defaultData,
        style: DEFAULT_NODE_STYLE,
      }));
    },
    [project, setNodes, nodes, structureLocked, canManage],
  );

  const onDragStart = (event, comp) => {
    event.dataTransfer.setData('application/reactflow', JSON.stringify(comp));
    event.dataTransfer.effectAllowed = 'move';
  };

  const onNodeClick = useCallback((event, node) => {
    if (event.target.closest('button')) return;
    // Trigger nodes are fixed for every flow and cannot be edited.
    if (node.type === 'triggerNode') return;
    setSelectedNode(node);
    setShowConfig(true);
  }, []);

  const onNodeMouseDown = useCallback((event) => {
    if (event.target.closest('button') || event.target.closest('.nodrag')) {
      event.preventDefault();
      event.stopPropagation();
    }
  }, []);

  const handleBackToFlows = () => {
    const params = new URLSearchParams();
    if (flowId) params.set('flowId', flowId);
    if (fromOnboarding) params.set('fromOnboarding', 'true');
    const query = params.toString();
    navigate(`/user/flow${query ? `?${query}` : ''}`);
  };

  const handleConfigUpdate = (nodeId, newData) => {
    setNodes((nds) => nds.map((node) =>
      node.id === nodeId ? { ...node, data: { ...node.data, ...newData } } : node
    ));
  };

  // ── Email save handler ──────────────────────────────────────────────────────
  const handleSaveEmailTemplate = async () => {
    if (!emailEditorRef.current) {
      setShowEmailEditor(false);
      setEditingEmailNode(null);
      return;
    }

    const nodeId = editingEmailNode?.id;
    let templateId = editingEmailNode?.data?.templateId;

    try {
      let html = '', mjml = '', compiledHtml = '';
      try { html = emailEditorRef.current.getHtml() || ''; } catch { /**/ }
      try { mjml = emailEditorRef.current.getMjml() || ''; } catch { /**/ }
      try { compiledHtml = (await emailEditorRef.current.getExportedHtml()) || ''; } catch { compiledHtml = html; }

      let processedHtml = compiledHtml || html;
      let processedMjml = mjml;
      try {
        if (html) processedHtml = (await processTemplateImages(compiledHtml || html, templateId)) || processedHtml;
        if (mjml) processedMjml = (await processTemplateImages(mjml, templateId)) || processedMjml;
      } catch { /**/ }

      if (templateId && templateId !== 'new-template') {
        const payload = {
          name: editingEmailNode?.data?.label || 'Email Template',
          html_content: processedHtml,
          mjml_content: processedMjml,
          is_draft: true,
        };
        try {
          await updateTemplate(templateId, 'email', payload);
          message.success('Template saved!');
          lastSavedGraphRef.current = '';
          setWorkflowVersion('draft');
          setSaveStatus('saved');
          setLastSavedAt(new Date());
        } catch (err) {
          // If 404/403, it means the template doesn't exist (e.g. from imported workflow)
          if (err?.response?.status === 404 || err?.response?.status === 403 || err?.response?.data?.message?.includes("not found")) {
            try {
              let newTemplateId = uuidv4();
              const res = await createTemplate(newTemplateId, 'email', payload.name);
              newTemplateId = res.data?.data?.template?.id || res.data?.data?.id || res.data?.id || newTemplateId;
              await updateTemplate(newTemplateId, 'email', payload);

              // Update node in the graph with the new ID
              setNodes((nds) => nds.map((n) => n.id === nodeId ? { ...n, data: { ...n.data, templateId: newTemplateId } } : n));
              message.success('Template saved to your store!');
              lastSavedGraphRef.current = '';
              setWorkflowVersion('draft');
              setSaveStatus('saved');
              setLastSavedAt(new Date());
            } catch (createErr) {
              message.warning('Could not create or save new template, content preserved locally');
            }
          } else {
            message.warning('Could not save to server, content preserved locally');
          }
        }
      }

      // Template saved to API above — flow node state is intentionally NOT updated.
      // emailContent / mjmlContent must never enter ReactFlow state to avoid
      // triggering auto-save or causing node resets when the editor closes.
    } catch (error) {
      logger.error('Error saving email template:', error);
      message.error('Error saving email template');
    } finally {
      setTimeout(() => {
        setShowEmailEditor(false);
        setEditingEmailNode(null);
        // Update URL without triggering React Router navigation
        const p = new URLSearchParams(window.location.search);
        p.delete('templateId');
        window.history.replaceState(null, '', `${window.location.pathname}?${p.toString()}`);
      }, 50);
    }
  };

  // ── Publish ─────────────────────────────────────────────────────────────────
  const publishWorkflow = async () => {
    if (!workflowId) { message.warning('No workflow to publish'); return; }
    if (!nodes.some((n) => n.type === 'triggerNode')) {
      message.error('Workflow must have a trigger node before publishing.');
      return;
    }

    setSaveStatus('saving'); setIsSaving(true);

    const payload = {
      graph_json: {
        nodes: nodes.map((n) => ({
          id: n.id, type: n.type, position: n.position, style: n.style,
          data: (({ emailContent, mjmlContent, smsContent, ...rest }) => rest)(n.data || {}),
        })),
        edges,
      },
      status: 'published',
    };

    try {
      message.loading({ content: 'Publishing workflow...', key: 'publishFlow' });
      await updateWorkflow(workflowId, payload);
      message.success('Workflow published successfully!');
      setSaveStatus('saved');
      setWorkflowVersion('published');
      setLastSavedAt(new Date());
      lastSavedGraphRef.current = serializeGraph(nodes, edges);
    } catch (error) {
      logger.error('Publish failed:', error);
      message.error('Failed to publish workflow');
      setSaveStatus('unsaved');
    } finally {
      setIsSaving(false);
    }
  };

  // ── Delete draft ────────────────────────────────────────────────────────────
  const handleDeleteDraft = () => {
    if (!workflowId) { message.warning('No workflow selected'); return; }
    Modal.confirm({
      title: 'Delete Draft',
      content: 'Are you sure you want to delete this draft? The workflow will revert to the last published version.',
      okText: 'Delete', okType: 'danger', cancelText: 'Cancel',
      onOk: async () => {
        setIsSaving(true); setSaveStatus('saving');
        try {
          await deleteWorkflowDraft(workflowId);
          message.success('Draft deleted successfully');

          if (flowId) {
            const response = await getWorkflow(flowId);
            const workflowData = response.data?.data;
            if (workflowData?.workflow?.active_version?.graph_json) {
              const graphData = workflowData.workflow.active_version.graph_json;
              setNodes(graphData.nodes || []);
              setEdges(graphData.edges || []);
              setWorkflowVersion('published');
              setLastSavedVersion(workflowData.workflow.active_version.version_number);
              setLastSavedAt(new Date(workflowData.workflow.active_version.updated_at));
              lastSavedGraphRef.current = serializeGraph(graphData.nodes || [], graphData.edges || []);
              if (graphData.nodes?.length > 0) {
                setIdCounter(Math.max(...graphData.nodes.map((n) => parseInt(n.id) || 0), 0) + 1);
              }
            } else {
              setNodes([]); setEdges([]);
              setWorkflowVersion(null); setLastSavedVersion(null); setLastSavedAt(null);
              lastSavedGraphRef.current = serializeGraph([], []);
            }
          }
          setSaveStatus('saved');
        } catch {
          message.error('Failed to delete draft');
          setSaveStatus('unsaved');
        } finally {
          setIsSaving(false);
        }
      },
    });
  };

  // ── File load / local storage helpers ──────────────────────────────────────
  const loadWorkflowFromFile = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const workflow = JSON.parse(e.target.result);
        setIdCounter(Math.max(...workflow.nodes.map((n) => parseInt(n.id) || 0)) + 1);
        setNodes(workflow.nodes.map((n) => ({ ...n, style: DEFAULT_NODE_STYLE })));
        setEdges(workflow.edges);
      } catch { message.error('Error loading workflow file'); }
    };
    reader.readAsText(file);
  };

  const loadWorkflowFromStorage = (workflow) => {
    setIdCounter(Math.max(...workflow.nodes.map((n) => parseInt(n.id) || 0)) + 1);
    setNodes(workflow.nodes.map((n) => ({ ...n, style: DEFAULT_NODE_STYLE })));
    setEdges(workflow.edges);
  };

  const deleteWorkflowFromStorage = (wfId) => {
    const updated = savedWorkflows.filter((w) => w.id !== wfId);
    localStorage.setItem('workflows', JSON.stringify(updated));
    setSavedWorkflows(updated);
  };

  // ────────────────────────────────────────────────────────────────────────────
  // Render
  // ────────────────────────────────────────────────────────────────────────────
  return (
    <div style={{
      height: '100vh', width: '100%',
      background: workflowVersion === 'draft'
        ? 'linear-gradient(to bottom, #fffbeb 0%, #f7fafc 100%)'
        : '#f7fafc',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      display: 'flex', flexDirection: 'column',
      transition: 'background 0.3s ease',
    }}>

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div style={{
        background: 'white', borderBottom: '1px solid #e2e8f0',
        padding: '16px 24px', display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
      }}>
        {/* Left: Title + version badge */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <h1 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#1a202c' }}>
            {workflowName || 'Communication Builder'}
          </h1>
          {workflowVersion && (
            <span style={{
              padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '600',
              background: workflowVersion === 'draft'
                ? 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)'
                : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: 'white',
              boxShadow: workflowVersion === 'draft' ? '0 2px 8px rgba(251,191,36,0.3)' : '0 2px 8px rgba(16,185,129,0.3)',
              display: 'flex', alignItems: 'center', gap: '4px',
            }}>
              {workflowVersion === 'draft' ? '📝 Draft' : '✓ Published'}
            </span>
          )}
        </div>

        {/* Center: Save status badge */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 16px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', minWidth: '200px', justifyContent: 'center' }}>
          {saveStatus === 'saving' && (
            <>
              <div style={{ width: '14px', height: '14px', border: '2px solid #e2e8f0', borderTopColor: '#6366f1', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
              <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
              <span style={{ fontSize: '13px', color: '#64748b', fontWeight: '500' }}>Saving draft...</span>
            </>
          )}
          {saveStatus === 'saved' && lastSavedAt && (
            <>
              <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#10b981', boxShadow: '0 0 8px rgba(16,185,129,0.5)' }} />
              <span style={{ fontSize: '13px', color: '#10b981', fontWeight: '600' }}>
                {workflowVersion === 'draft' ? 'Draft saved' : 'Published'}
              </span>
              <span style={{ fontSize: '12px', color: '#94a3b8' }}>{formatRelativeTime(lastSavedAt)}</span>
            </>
          )}
          {saveStatus === 'unsaved' && (
            <>
              <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#ef4444' }} />
              <span style={{ fontSize: '13px', color: '#ef4444', fontWeight: '500' }}>Unsaved changes</span>
            </>
          )}
        </div>

        {/* Right: Action buttons */}
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <input ref={fileInputRef} type="file" accept=".json" onChange={loadWorkflowFromFile} style={{ display: 'none' }} />
          {/* <button onClick={() => fileInputRef.current?.click()} style={{ background: 'white', color: '#4a5568', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '8px 16px', fontSize: '14px', fontWeight: '500', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Upload size={16} /> Load
          </button> */}

          <button
            onClick={handleBackToFlows}
            style={{
              background: 'white',
              color: '#334155',
              border: '1px solid #cbd5e1',
              borderRadius: '6px',
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            Back
          </button>

          {canManage && workflowVersion === 'draft' && (
            <button
              onClick={handleDeleteDraft} disabled={isSaving}
              style={{ background: 'white', color: '#ef4444', border: '1px solid #fecaca', borderRadius: '6px', padding: '8px 16px', fontSize: '14px', fontWeight: '500', cursor: isSaving ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '8px', opacity: isSaving ? 0.6 : 1 }}
              onMouseEnter={(e) => { if (!isSaving) { e.currentTarget.style.background = '#fef2f2'; e.currentTarget.style.borderColor = '#f87171'; } }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'white'; e.currentTarget.style.borderColor = '#fecaca'; }}
            >
              <Trash2 size={16} /> Delete Draft
            </button>
          )}

          {canManage && (
          <button
            onClick={publishWorkflow} disabled={isSaving}
            style={{ background: isSaving ? '#cbd5e0' : '#667eea', color: 'white', border: 'none', borderRadius: '6px', padding: '8px 16px', fontSize: '14px', fontWeight: '500', cursor: isSaving ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '8px', opacity: isSaving ? 0.7 : 1 }}
          >
            <Save size={16} />
            {isSaving ? 'Publishing...' : 'Publish'}
          </button>
          )}
        </div>
      </div>

      {/* ── Main content ────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', height: 'calc(100vh - 65px)' }}>

        {/* Canvas */}
        <div className="reactflow-wrapper" ref={reactFlowWrapper} style={{ flex: 1, position: 'relative' }}>
          {isLoadingFlow ? (
            <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <PurebrandLoader textMessage="Loading workflow..." />
            </div>
          ) : (
            <ReactFlow
              nodes={nodes} edges={edges}
              onNodesChange={onNodesChange} onEdgesChange={onEdgesChange}
              onConnect={onConnect} onNodeClick={onNodeClick}
              onNodeMouseDown={onNodeMouseDown} onDrop={onDrop} onDragOver={onDragOver}
              nodeTypes={nodeTypes}
              elementsSelectable
              nodesDraggable={canManage && !structureLocked}
              nodesConnectable={canManage && !structureLocked}
              edgesUpdatable={canManage && !structureLocked}
              onPaneClick={() => { setShowConfig(false); setSelectedNode(null); }}
              onNodesDelete={(deletedNodes) => {
                if (structureLocked) {
                  message.warning("This workflow's structure is locked. You can edit message content only.");
                  return false;
                }
                const triggerNodes = nodes.filter((n) => n.type === 'triggerNode');
                if (deletedNodes.some((n) => n.type === 'triggerNode') && triggerNodes.length <= 1) {
                  message.warning('Cannot delete the only trigger node. The workflow must have a trigger.');
                  return false;
                }
              }}
              onEdgesDelete={(deletedEdges) => {
                if (structureLocked) {
                  message.warning("This workflow's structure is locked. You can edit message content only.");
                  return false;
                }
              }}
              style={{ background: 'transparent' }}
              fitView minZoom={0.3} maxZoom={2}
              deleteKeyCode={!canManage || structureLocked || showEmailEditor || showSmsEditor ? null : ['Backspace', 'Delete']}
            >
              <Background variant="dots" gap={16} size={1} color="#e2e8f0" />
              <Controls style={{ background: 'white', borderRadius: '8px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }} />
              <MiniMap nodeColor="#cbd5e0" style={{ background: 'white', borderRadius: '8px', border: '1px solid #e2e8f0' }} />
            </ReactFlow>
          )}
        </div>

        {/* Sidebar */}
        <div style={{ width: '280px', background: 'white', borderLeft: '1px solid #e2e8f0', overflowY: 'auto', padding: '16px' }}>
          {/* Saved local workflows */}
          {savedWorkflows.length > 0 && (
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ margin: '0 0 12px 0', fontSize: '13px', fontWeight: '600', color: '#1a202c', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Saved Workflows</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '200px', overflowY: 'auto' }}>
                {savedWorkflows.map((workflow) => (
                  <div key={workflow.id} style={{ background: '#f7fafc', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '8px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <button onClick={() => loadWorkflowFromStorage(workflow)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '12px', color: '#4a5568', textAlign: 'left', flex: 1, padding: 0 }}>
                      {workflow.name}
                    </button>
                    <button onClick={() => deleteWorkflowFromStorage(workflow.id)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#ef4444', padding: '2px' }}>
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* View-only notice — the role can read this flow but not change it */}
          {!canManage && (
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <span style={{ fontSize: '16px' }}>👁️</span>
                <h3 style={{ margin: 0, fontSize: '13px', fontWeight: '600', color: '#1a202c', textTransform: 'uppercase', letterSpacing: '0.05em' }}>View only</h3>
              </div>
              <p style={{ margin: 0, fontSize: '13px', color: '#64748b', lineHeight: 1.5 }}>
                Your role can view this workflow but not edit or publish it.
              </p>
            </div>
          )}

          {/* Structure-locked notice — replaces the drag palette entirely */}
          {canManage && structureLocked && (
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <span style={{ fontSize: '16px' }}>🔒</span>
                <h3 style={{ margin: 0, fontSize: '13px', fontWeight: '600', color: '#1a202c', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Structure locked</h3>
              </div>
              <p style={{ margin: 0, fontSize: '13px', color: '#64748b', lineHeight: 1.5 }}>
                This workflow's steps and timing are fixed. Click a SMS step to edit its
                message content, then publish.
              </p>
            </div>
          )}

          {/* Drag-to-canvas: Actions */}
          {canManage && !structureLocked && (
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '13px', fontWeight: '600', color: '#1a202c', textTransform: 'uppercase', letterSpacing: '0.05em' }}>ACTIONS</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {availableNodes.actions.map((comp, i) => (
                <div
                  key={i}
                  draggable
                  onDragStart={(e) => onDragStart(e, comp)}
                  style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '12px', cursor: 'grab', display: 'flex', alignItems: 'center', gap: '12px', transition: 'all 0.2s' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#f7fafc'; e.currentTarget.style.borderColor = '#cbd5e0'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'white'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
                >
                  <span style={{ fontSize: '18px' }}>{comp.icon}</span>
                  <span style={{ fontSize: '14px', color: '#4a5568', fontWeight: '500' }}>{comp.label}</span>
                  <span style={{ marginLeft: 'auto', fontSize: '16px', color: '#cbd5e0' }}>⋮⋮</span>
                </div>
              ))}
            </div>
          </div>
          )}

          {/* Drag-to-canvas: Timing */}
          {canManage && !structureLocked && availableNodes.timings.length > 0 && (
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '13px', fontWeight: '600', color: '#1a202c', textTransform: 'uppercase', letterSpacing: '0.05em' }}>TIMING</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {availableNodes.timings.map((comp, i) => (
                <div
                  key={i}
                  draggable
                  onDragStart={(e) => onDragStart(e, comp)}
                  style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '12px', cursor: 'grab', display: 'flex', alignItems: 'center', gap: '12px', transition: 'all 0.2s' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#f7fafc'; e.currentTarget.style.borderColor = '#cbd5e0'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'white'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
                >
                  <span style={{ fontSize: '18px' }}>{comp.icon}</span>
                  <span style={{ fontSize: '14px', color: '#4a5568', fontWeight: '500' }}>{comp.label}</span>
                  <span style={{ marginLeft: 'auto', fontSize: '16px', color: '#cbd5e0' }}>⋮⋮</span>
                </div>
              ))}
            </div>
          </div>
          )}
        </div>
      </div>

      {/* ── Config panel (right-side drawer) ───────────────────────────────── */}
      {showConfig && (
        <ConfigPanel
          selectedNode={selectedNode}
          flowTypeCode={flowTypeCode}
          flowId={flowId}
          locked={structureLocked || !canManage}
          readOnly={!canManage}
          onClose={() => { setShowConfig(false); setSelectedNode(null); }}
          onUpdate={handleConfigUpdate}
          onOpenEmailEditor={async (node) => {
            // Deselect all nodes so stray key events from GrapesJS can't delete them
            setNodes((nds) => nds.map((n) => ({ ...n, selected: false })));
            let nodeToEdit = node;
            // Pre-registering a template is a write; view-only roles open the editor as-is.
            if (canManage && !node?.data?.templateId) {
              let newTemplateId = uuidv4();
              try {
                const res = await createTemplate(newTemplateId, 'email', node?.data?.label || 'New Email');
                newTemplateId = res.data?.data?.template?.id || res.data?.data?.id || res.data?.id || newTemplateId;
              } catch (e) {
                logger.warn('[onOpenEmailEditor] Could not pre-register template:', e?.message);
              }
              nodeToEdit = { ...node, data: { ...node.data, templateId: newTemplateId } };
              setNodes((nds) => nds.map((n) => n.id === node.id ? { ...n, data: { ...n.data, templateId: newTemplateId } } : n));
            }
            setEditingEmailNode(nodeToEdit);
            setShowEmailEditor(true);
            const p = new URLSearchParams(window.location.search);
            p.set('templateId', nodeToEdit?.data?.templateId || '');
            window.history.replaceState(null, '', `${window.location.pathname}?${p.toString()}`);
          }}
          onOpenSmsEditor={async (node) => {
            // Deselect all nodes so stray key events can't delete them while editor is open
            setNodes((nds) => nds.map((n) => ({ ...n, selected: false })));
            let nodeToEdit = node;
            if (canManage && !node?.data?.templateId) {
              let newTemplateId = uuidv4();
              try {
                const res = await createTemplate(newTemplateId, 'sms', node?.data?.label || 'New SMS');
                newTemplateId = res.data?.data?.template?.id || res.data?.data?.id || res.data?.id || newTemplateId;
              } catch (e) {
                logger.warn('[onOpenSmsEditor] Could not pre-register template:', e?.message);
              }
              nodeToEdit = { ...node, data: { ...node.data, templateId: newTemplateId } };
              setNodes((nds) => nds.map((n) =>
                n.id === node.id ? { ...n, data: { ...n.data, templateId: newTemplateId } } : n
              ));
            }
            setEditingSmsNode(nodeToEdit);
            setShowSmsEditor(true);
          }}

        />
      )}

      {/* ── SMS editor modal ────────────────────────────────────────────────── */}
      {showSmsEditor && editingSmsNode && (
        <SmsEditorModal
          node={editingSmsNode}
          flowId={flowId}
          readOnly={!canManage}
          onClose={() => { setShowSmsEditor(false); setEditingSmsNode(null); }}
          onSave={(newTemplateId) => {
            if (newTemplateId) {
              setNodes((nds) => nds.map((n) => n.id === editingSmsNode.id ? { ...n, data: { ...n.data, templateId: newTemplateId } } : n));
            }
            lastSavedGraphRef.current = '';
            setWorkflowVersion('draft');
            setSaveStatus('saved');
            setLastSavedAt(new Date());
            setShowSmsEditor(false);
            setEditingSmsNode(null);
          }}
        />
      )}

      {/* ── Email editor full-screen overlay ───────────────────────────────── */}
      <div
        className="email-editor-overlay"
        style={{
          position: 'fixed', top: 0, left: '260px', right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2000,
          display: showEmailEditor && editingEmailNode ? 'flex' : 'none',
          alignItems: 'center', justifyContent: 'center',
        }}
      >
        <div style={{ width: 'calc(100% - 40px)', height: '95%', backgroundColor: 'white', borderRadius: '12px', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
          {/* Header */}
          <div style={{ padding: '20px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#f7fafc' }}>
            <div>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#1a202c' }}>
                Edit Email: {editingEmailNode?.data?.label || 'Email'}
              </h2>
              <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#64748b' }}>
                {canManage
                  ? 'Design your email template using the drag & drop editor'
                  : 'View only — your role cannot save changes to this template'}
              </p>
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              {canManage && (
              <button
                onClick={handleSaveEmailTemplate}
                style={{ padding: '10px 20px', background: '#667eea', color: 'white', border: 'none', borderRadius: '6px', fontSize: '14px', fontWeight: '500', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <Save size={16} /> Save &amp; Close
              </button>
              )}
              <button
                onClick={() => {
                  setTimeout(() => {
                    setShowEmailEditor(false);
                    setEditingEmailNode(null);
                    const p = new URLSearchParams(window.location.search);
                    p.delete('templateId');
                    window.history.replaceState(null, '', `${window.location.pathname}?${p.toString()}`);
                  }, 50);
                }}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '8px', display: 'flex', alignItems: 'center', color: '#64748b' }}
              >
                <X size={24} />
              </button>
            </div>
          </div>

          {/* Editor */}
          <div style={{ flex: 1, overflow: 'hidden' }}>
            {showEmailEditor && editingEmailNode && (
              <GrapesJsEditor
                key={editingEmailNode.id}
                ref={emailEditorRef}
                nodeId={editingEmailNode.id}
                templateId={editingEmailNode.data?.templateId || 'new-template'}
                flowId={flowId}
              />
            )}
          </div>
        </div>
      </div>

    </div>
  );
};

// CommunicationFlowBuilder — thin entry point; provides ReactFlow context & global styles
// ─────────────────────────────────────────────────────────────────────────────
const CommunicationFlowBuilder = () => {
  const { userProfile } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const fromOnboarding = searchParams.get('fromOnboarding') === 'true';
  const canAccessFlow = userProfile?.complete_profile || fromOnboarding;

  if (!canAccessFlow) {
    return <IncompleteProfile />;
  }

  return (
    <ReactFlowProvider>
      <style>{`
        .react-flow__edge-path { stroke: #cbd5e0; stroke-width: 2px; transition: stroke 0.2s, stroke-width 0.2s; }
        .react-flow__edge.selected .react-flow__edge-path { stroke: #667eea !important; stroke-width: 3px !important; }
        .react-flow__edge:hover .react-flow__edge-path { stroke: #a0aec0; stroke-width: 3px; }
        @media (max-width: 1100px) {
          .email-editor-overlay { left: 0 !important; }
          .sms-editor-modal { max-height: 90vh !important; overflow-y: auto !important; }
          .sms-editor-layout { flex-direction: column !important; }
          .sms-editor-sidebar { width: 100% !important; border-left: none !important; border-top: 1px solid #e2e8f0 !important; max-height: 200px !important; }
        }
      `}</style>
      <WorkflowBuilder />
    </ReactFlowProvider>
  );
};

export default CommunicationFlowBuilder;